/**
 * AV_Band — the Anchored Volume Band's core, in one place.
 *
 * WHY THIS FILE EXISTS
 *
 * The band's Effective Line, its two HMAs, its edges and the slow line's colour
 * were all computed inside TradingChart's render effect and then thrown away, so
 * nothing outside the chart could read them. Meanwhile `avTrendSignal.ts` on the
 * backend rebuilt the SAME effective line and the SAME slow HMA to drive
 * DeltaMiner. Two copies of one calculation, and no test comparing them.
 *
 * EXTRACT, DO NOT WIDEN. `computeAvTrend` / `computeAvTrendSeries` are reached
 * by live order-placing code, so their signatures and return types are
 * unchanged; they now call this for the parts they already computed. Same
 * discipline as the BBR/Wave oscillator extraction: the drawn band and anything
 * acting on it are guaranteed to be the same numbers because there is only one
 * copy of them.
 *
 * WHAT THE USER ACTUALLY SEES. The chart's defaults draw the SLOW line only —
 * `showFast` and `showBand` are both off. So `slowHue` is the series behind
 * "the AV_Band changed colour", and `slow` is the line behind "price closed
 * above/below the AV_Band". The band edges are returned too, but they are the
 * less-seen object, not the headline one.
 *
 * PURE: no React, no lightweight-charts, no colours. The caller maps `slowHue`
 * to its own theme's hues, so this file has no opinion about appearance.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { linreg } from './candleTransforms';
import { computeWVol } from './wvol';
import { hmaSeries } from './hma';

export interface AvBandOptions {
  fastPeriod: number;
  slowPeriod: number;
  /** Effective-Line boundary rule. false (logic1, the default): a strong-up
   *  score picks the zone HIGH. true (logic2): inverted. */
  effLogic2?: boolean;
  /** Regression length for the slow line's colour source. The chart passes
   *  settings.candle.clnLength; the colour is deliberately defined on the
   *  regression of the RAW closes so it means the same thing on every display
   *  candle type. */
  clnLength: number;
}

export interface AvBandSeries {
  /** The Effective Line: a High/Low zone latched from WVol spike bars, resolved
   *  per bar by the OverView score. NaN before the first spike. */
  eff: number[];
  fast: number[];
  slow: number[];
  /** Band edges = max/min of the two HMAs. null where either is not yet finite. */
  upper: Array<number | null>;
  lower: Array<number | null>;
  /** fast >= slow — the band's own bull/bear state. null where undefined. */
  bandUp: Array<boolean | null>;
  /** The slow line's three-state hue: +1 up, -1 down, 0 neutral, null before it
   *  is defined. Both the regression close AND the slow line's own slope must
   *  agree for a directional value; a disagreement is neutral, not a guess. */
  slowHue: Array<-1 | 0 | 1 | null>;
}

/**
 * The Effective Line and the slow HMA — the half `avTrendSignal` also needs.
 * Split out so the trend-band walk can take exactly what it uses without
 * paying for the fast HMA or the colour series.
 */
export function computeAvEffective(
  bars: CandleRow[],
  scoreByTime: Map<number, number>,
  slowPeriod: number,
  effLogic2?: boolean,
): { eff: number[]; slow: number[] } {
  const n = bars.length;
  const eff = new Array<number>(n).fill(NaN);
  const wv = computeWVol(bars);
  let zHigh = NaN, zLow = NaN;
  for (let i = 0; i < n; i++) {
    if (wv[i]?.isSpike) { zHigh = Number(bars[i].high); zLow = Number(bars[i].low); }
    if (!Number.isFinite(zHigh) || !Number.isFinite(zLow)) continue;
    const sc = scoreByTime.get(bars[i].time as number);
    if (sc != null && sc > 20) eff[i] = effLogic2 ? zLow : zHigh;
    else if (sc != null && sc < -20) eff[i] = effLogic2 ? zHigh : zLow;
    else eff[i] = (zHigh + zLow) / 2;
  }
  return { eff, slow: hmaSeries(eff, slowPeriod) };
}

export function computeAvBandSeries(
  bars: CandleRow[],
  scoreByTime: Map<number, number>,
  opts: AvBandOptions,
): AvBandSeries {
  const n = bars.length;
  const { eff, slow } = computeAvEffective(bars, scoreByTime, opts.slowPeriod, opts.effLogic2);
  const fast = hmaSeries(eff, opts.fastPeriod);

  const upper = new Array<number | null>(n).fill(null);
  const lower = new Array<number | null>(n).fill(null);
  const bandUp = new Array<boolean | null>(n).fill(null);
  for (let i = 0; i < n; i++) {
    const f = fast[i], s = slow[i];
    if (!Number.isFinite(f) || !Number.isFinite(s)) continue;
    upper[i] = Math.max(f, s);
    lower[i] = Math.min(f, s);
    bandUp[i] = f >= s;
  }

  // Slow-line hue. Defined on the regression of the RAW closes, on the SAME bar
  // grid as the band, so no time-map lookup is needed and the result does not
  // depend on the chart's display candle type.
  const lr = linreg(bars.map(b => Number(b.close)), opts.clnLength);
  const slowHue = new Array<-1 | 0 | 1 | null>(n).fill(null);
  for (let i = 0; i < n; i++) {
    const s = slow[i];
    const prev = i > 0 ? slow[i - 1] : NaN;
    const l = Number(lr[i]);
    if (!Number.isFinite(s) || !Number.isFinite(prev) || !Number.isFinite(l)) continue;
    slowHue[i] = (l > s && s > prev) ? 1 : (l < s && s < prev) ? -1 : 0;
  }

  return { eff, fast, slow, upper, lower, bandUp, slowHue };
}
